import { useCallback, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import {
  LIST_VIEW_RESTORE,
  blockListViewScrollWrite,
  mergeListViewFilters,
  readListViewSnapshot,
  takePendingListViewScroll,
  writeListViewSnapshot,
  type ListViewSnapshot,
} from '../utils/listViewSession';

type FiltersObject = Record<string, unknown>;
type FiltersFactory<T extends FiltersObject> = T | (() => T);

export type ListViewCoords<TFilters extends FiltersObject> = {
  applied: TFilters;
  page: number;
  pageSize: number;
};

function resolveFilters<T extends FiltersObject>(value: FiltersFactory<T>): T {
  return typeof value === 'function' ? (value as () => T)() : value;
}

function cloneFilters<T extends FiltersObject>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function filtersEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

function persistSnapshot<TFilters extends FiltersObject>(
  listViewKey: string,
  applied: TFilters,
  page: number,
  pageSize: number,
  options?: { clearScroll?: boolean },
): void {
  const current = readListViewSnapshot<TFilters>(listViewKey);
  let keepScroll = current?.scroll;
  if (options?.clearScroll) {
    blockListViewScrollWrite(listViewKey);
    takePendingListViewScroll(listViewKey);
    keepScroll = undefined;
  } else {
    const pending = takePendingListViewScroll(listViewKey);
    keepScroll = keepScroll ?? pending;
  }
  const next: ListViewSnapshot<TFilters> = {
    v: current?.v ?? 1,
    applied: cloneFilters(applied),
    page,
    pageSize,
    ...(keepScroll ? { scroll: keepScroll } : {}),
  };
  writeListViewSnapshot(listViewKey, next);
}

function readHydratedState<TFilters extends FiltersObject>(
  listViewKey: string,
  defaultFilters: FiltersFactory<TFilters>,
  defaultPageSize: number,
): { filters: TFilters; page: number; pageSize: number } {
  const defaults = resolveFilters(defaultFilters);
  const snapshot = readListViewSnapshot<TFilters>(listViewKey);
  const filters = snapshot
    ? mergeListViewFilters(defaults, snapshot.applied)
    : cloneFilters(defaults);
  if (!LIST_VIEW_RESTORE.pagination || !snapshot) {
    return { filters, page: 1, pageSize: defaultPageSize };
  }
  return { filters, page: snapshot.page, pageSize: snapshot.pageSize };
}

/**
 * 页面列表的 draft / applied / 分页状态机，并写入当前 Tab 的列表会话。
 * 请求仍由页面发起；请求参数必须使用返回的 applied / page / pageSize。
 * 文本失焦/回车走 applyIfChanged；下拉/日期选完走 patchAndApply。无变化不请求。
 * 查询、重置、翻页会清掉表体滚动；滚动由 IpcTable 的 listViewKey 写入同一快照。
 */
export function useListViewQuery<TFilters extends FiltersObject>(options: {
  listViewKey: string;
  defaultFilters: FiltersFactory<TFilters>;
  emptyFilters: FiltersFactory<TFilters>;
  defaultPageSize?: number;
}): {
  draft: TFilters;
  setDraft: Dispatch<SetStateAction<TFilters>>;
  patchDraft: (patch: Partial<TFilters>) => void;
  applied: TFilters;
  page: number;
  pageSize: number;
  /** draft 与 applied 相同则返回 null，页面不应再请求。 */
  applyIfChanged: (next?: TFilters) => ListViewCoords<TFilters> | null;
  /** 下拉 / 日期选完：合并 patch 后立刻 apply；无变化返回 null。 */
  patchAndApply: (patch: Partial<TFilters>) => ListViewCoords<TFilters> | null;
  reset: () => ListViewCoords<TFilters>;
  clearAll: () => ListViewCoords<TFilters>;
  commitApplied: (next: TFilters, page?: number) => ListViewCoords<TFilters>;
  /** 只合并部分 applied（如删 chip、客户端即时筛选），不把未查询的 draft 整份覆盖成 applied。 */
  patchApplied: (patch: Partial<TFilters>) => ListViewCoords<TFilters>;
  setPagination: (page: number, pageSize: number) => ListViewCoords<TFilters>;
} {
  const { listViewKey, defaultPageSize = 20 } = options;
  const defaultFiltersRef = useRef(options.defaultFilters);
  const emptyFiltersRef = useRef(options.emptyFilters);
  defaultFiltersRef.current = options.defaultFilters;
  emptyFiltersRef.current = options.emptyFilters;

  const [hydrated] = useState(() => (
    readHydratedState(listViewKey, options.defaultFilters, defaultPageSize)
  ));
  const [draftState, setDraft] = useState<TFilters>(() => cloneFilters(hydrated.filters));
  const [appliedState, setApplied] = useState<TFilters>(hydrated.filters);
  const [pageState, setPageState] = useState(hydrated.page);
  const [pageSizeState, setPageSizeState] = useState(hydrated.pageSize);
  const draftRef = useRef(draftState);
  const appliedRef = useRef(appliedState);
  const pageRef = useRef(pageState);
  const pageSizeRef = useRef(pageSizeState);
  draftRef.current = draftState;
  appliedRef.current = appliedState;
  pageRef.current = pageState;
  pageSizeRef.current = pageSizeState;

  useLayoutEffect(() => {
    persistSnapshot(listViewKey, appliedRef.current, pageRef.current, pageSizeRef.current);
  }, [listViewKey]);

  const commitApplied = useCallback((next: TFilters, page = 1): ListViewCoords<TFilters> => {
    const appliedNext = cloneFilters(next);
    const nextPage = page;
    const nextPageSize = pageSizeRef.current;
    draftRef.current = cloneFilters(appliedNext);
    appliedRef.current = appliedNext;
    pageRef.current = nextPage;
    setDraft(cloneFilters(appliedNext));
    setApplied(appliedNext);
    setPageState(nextPage);
    persistSnapshot(listViewKey, appliedNext, nextPage, nextPageSize, { clearScroll: true });
    return { applied: appliedNext, page: nextPage, pageSize: nextPageSize };
  }, [listViewKey]);

  const applyIfChanged = useCallback((next?: TFilters): ListViewCoords<TFilters> | null => {
    const candidate = cloneFilters(next ?? draftRef.current);
    if (filtersEqual(candidate, appliedRef.current)) {
      draftRef.current = candidate;
      setDraft(candidate);
      return null;
    }
    return commitApplied(candidate, 1);
  }, [commitApplied]);

  const patchAndApply = useCallback((patch: Partial<TFilters>): ListViewCoords<TFilters> | null => {
    return applyIfChanged({ ...draftRef.current, ...patch });
  }, [applyIfChanged]);

  const reset = useCallback((): ListViewCoords<TFilters> => {
    return commitApplied(resolveFilters(defaultFiltersRef.current), 1);
  }, [commitApplied]);

  const clearAll = useCallback((): ListViewCoords<TFilters> => {
    return commitApplied(resolveFilters(emptyFiltersRef.current), 1);
  }, [commitApplied]);

  const patchDraft = useCallback((patch: Partial<TFilters>) => {
    setDraft(prev => ({ ...prev, ...patch }));
  }, []);

  const patchApplied = useCallback((patch: Partial<TFilters>): ListViewCoords<TFilters> => {
    const appliedNext = { ...appliedRef.current, ...patch };
    const draftNext = { ...draftRef.current, ...patch };
    const nextPage = 1;
    const nextPageSize = pageSizeRef.current;
    draftRef.current = draftNext;
    appliedRef.current = appliedNext;
    pageRef.current = nextPage;
    setDraft(draftNext);
    setApplied(appliedNext);
    setPageState(nextPage);
    persistSnapshot(listViewKey, appliedNext, nextPage, nextPageSize, { clearScroll: true });
    return { applied: appliedNext, page: nextPage, pageSize: nextPageSize };
  }, [listViewKey]);

  const setPagination = useCallback((page: number, pageSize: number): ListViewCoords<TFilters> => {
    const nextPage = Math.max(1, page);
    const nextPageSize = pageSize > 0 ? pageSize : defaultPageSize;
    const pageChanged = nextPage !== pageRef.current || nextPageSize !== pageSizeRef.current;
    pageRef.current = nextPage;
    pageSizeRef.current = nextPageSize;
    setPageState(nextPage);
    setPageSizeState(nextPageSize);
    persistSnapshot(listViewKey, appliedRef.current, nextPage, nextPageSize, {
      clearScroll: pageChanged,
    });
    return { applied: appliedRef.current, page: nextPage, pageSize: nextPageSize };
  }, [defaultPageSize, listViewKey]);

  return {
    draft: draftState,
    setDraft,
    patchDraft,
    applied: appliedState,
    page: pageState,
    pageSize: pageSizeState,
    applyIfChanged,
    patchAndApply,
    reset,
    clearAll,
    commitApplied,
    patchApplied,
    setPagination,
  };
}
